import React from 'react';
import ReactDOM from 'react-dom';
import {MemoryRouter, BrowserRouter } from 'react-router-dom';
import 'webrtc-adapter';
import * as Nucleus from '@lifesize/nucleus';
import { redirectToLauncherPage, isProduction  } from 'utils/UrlUtil';
import { Analytics, Observer, AnalyticsEnvironment } from '@lifesize/web-clients-core';
import ExceptionReporting from 'utils/ExceptionReporting';
import initializeNucleus from 'utils/init-utils';
import Logger from 'utils/logger';
import Api from './api/Api';
import createStore from './store/createStore';
import calendarObserver from './observers/calendar';
import inviteObserver from './observers/invite';
import notificationObserver from './observers/notification';
import permissionsObserver from './observers/permissions';
import desktopObserver from './observers/desktop';
import callObserver from './observers/call';
import AppContainer from './containers/AppContainer';
import { initialize as initializeAzureApi } from './api/azure-api';
import { isWebAppSupported } from 'utils/browser-utils';
import { basePath } from 'constants/Application';

const observables = [
  inviteObserver,
  permissionsObserver,
  notificationObserver,
  calendarObserver,
  callObserver
];

Logger.useDefaults();
Logger.info(`Lifesize Web App v${process.env.REACT_APP_VERSION}`);

const isDesktop = process.env.REACT_APP_DESKTOP === 'true';

// ========================================================
// Determine browser support
// ========================================================
if (!isDesktop && !isWebAppSupported()) {
  redirectToLauncherPage();
}

// ========================================================
// Developer Tools Setup
// ========================================================
if (process.env.REACT_APP_DEBUG) {
  if (window.devToolsExtension) {
    window.devToolsExtension.open();
  }
} else if (!process.env.REACT_APP_DEBUG) {
  ExceptionReporting.initialize();
}


if (!window.lifesize) window.lifesize = {};
if (!window.lifesize.apiDomain) window.lifesize.apiDomain = process.env.REACT_APP_API_DOMAIN;


const initAnalytics = () => {
  // Don't trigger analytics if iFrame is detected
  if (window.self === window.top) {
    let environment;
    if (process.env.REACT_APP_DEBUG) {
      environment = AnalyticsEnvironment.integration;
    }
    else if (isProduction()) {
      environment = AnalyticsEnvironment.production;
    }
    else {
      environment = AnalyticsEnvironment.stage;
    }
    Analytics.init('AP-NOLE8QZH1MTP-2', environment);

    const type = (isDesktop) ? 'desktop' : 'web';

    Analytics.setContext({
      type,
      version: process.env.REACT_APP_VERSION
    });
  }
}

const initObservables = (store) => {
  Observer.createObservable(store, observables);
};

const initDesktopAPI = (store) => {
  if (isDesktop) {
    Api.initialize(store);
    observables.push(desktopObserver);
  }
}

const getStore = async () => {
  const store = await createStore({});
  return store;
}

const render = async (store) => {
  if (window?.frameElement?.id?.includes('microsoft.com')) {
    return;
  }
  /*
    webmodule bucket is not able to handle two level routes. To get around it,
    webmodule is loaded with MemoryRouter,
    so that it will load root route when webmodule is reloaded.
  */
  const Router = basePath ? MemoryRouter : BrowserRouter;

  // MemoryRouter will work only if it is the root element in the DOM.
  ReactDOM.render(
    <Router basename={basePath}>
      <AppContainer store={store} />
    </Router>,
    document.getElementById('root')
  );
};

initAnalytics();

getStore().then(async (returnedStore) => {
  const store = returnedStore;
  if (!window.lifesize) window.lifesize = {};
  window.lifesize.store = store;
  window.lifesize.nucleus = Nucleus;
  try {
    initDesktopAPI(store);
    initObservables(store);
    initializeAzureApi();
    await initializeNucleus(store);
  } catch (e) {
    Logger.error(`Initialization error ${e}`);
  }
  render(store);
});
